import { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, FileText, Upload, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '@/components/shadcn-ui/button';
import { Input } from '@/components/shadcn-ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn-ui/dialog';
import { appAxios } from '@/network/base/appAxios';
import { uploadToS3 } from '@/utils/s3presigned';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const newRow = () => ({ id: crypto.randomUUID(), name: '', file: null });
const shortFileName = key => key?.split('/').pop() ?? key;

const CardShell = ({ index, onDelete, deleteDisabled, children }) => (
  <div className="rounded-xl border border-border bg-card shadow-sm flex flex-col overflow-hidden">
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/40">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Document {index}
      </span>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          disabled={deleteDisabled}
          className={cn(
            'p-1 rounded transition',
            deleteDisabled
              ? 'opacity-30 cursor-not-allowed text-muted-foreground'
              : 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
          )}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 px-4 py-3">
      {children}
    </div>
  </div>
);

const CountyPermitUpload = ({ permitDetails, permitId, userId }) => {
  const [loading, setLoading] = useState(false);
  const [countyPermit, setCountyPermit] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [rows, setRows] = useState([newRow()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileRefs = useRef({});
  const [addDoc, setAddDoc] = useState({ name: '', file: null });
  const [isAdding, setIsAdding] = useState(false);
  const addFileRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await appAxios.get('/Admin/v1.0/get-county-permit', {
        params: { permitId, userId },
      });
      setCountyPermit(response.data.data);
      setShowNewForm(false);
    } catch {
      setCountyPermit(null);
      setShowNewForm(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permitDetails?.base_fees_paid) fetchDocuments();
  }, [permitDetails]);

  const addRow = () => setRows(prev => [...prev, newRow()]);
  const removeRow = id => rows.length > 1 && setRows(prev => prev.filter(r => r.id !== id));
  const updateName = (id, val) => setRows(prev => prev.map(r => (r.id === id ? { ...r, name: val } : r)));
  const attachFile = (id, file) => setRows(prev => prev.map(r => (r.id === id ? { ...r, file } : r)));
  const handleFileChange = (id, e) => {
    const file = e.target.files[0];
    if (file) attachFile(id, file);
    e.target.value = '';
  };

  const validateRows = () => {
    for (const row of rows) {
      if (!row.name.trim()) { toast.error('Each document must have a name.'); return false; }
      if (!row.file) { toast.error(`Please attach a file for "${row.name || 'document'}".`); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateRows()) return;
    setIsSubmitting(true);
    try {
      const documents = await Promise.all(
        rows.map(async row => {
          const { data, error } = await uploadToS3(row.file, 'county-permits', true);
          if (error) throw new Error(`Failed to upload "${row.name}"`);
          return { name: row.name.trim(), document: data.key };
        })
      );
      await appAxios.post('/Admin/v1.0/add-county-permit', { userId, permitId, documents });
      toast.success('Documents submitted successfully.');
      setRows([newRow()]);
      fetchDocuments();
    } catch (err) {
      toast.error('Submission failed.', { description: err.response?.data?.message || err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const viewDocument = async key => {
    try {
      const response = await appAxios.get('/Admin/v1.0/portal-proof-document', { params: { key } });
      window.open(response.data?.data?.viewUrl, '_blank');
    } catch {
      toast.error('Failed to open document.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await appAxios.delete('/Admin/v1.0/county-permit-document', {
        params: { userId, permitId, documentId: deleteTarget.docId },
      });
      toast.success(`"${deleteTarget.docName}" removed.`);
      setDeleteTarget(null);
      fetchDocuments();
    } catch (err) {
      toast.error('Delete failed.', { description: err.response?.data?.message || err.message });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddSingle = async () => {
    if (!addDoc.name.trim()) { toast.error('Document name is required.'); return; }
    if (!addDoc.file) { toast.error('Please choose a file.'); return; }
    setIsAdding(true);
    try {
      const { data, error } = await uploadToS3(addDoc.file, 'county-permits', true);
      if (error) throw error;
      await appAxios.post('/Admin/v1.0/add-county-permit', {
        userId,
        permitId,
        documents: [{ name: addDoc.name.trim(), document: data.key }],
      });
      toast.success('Document added successfully.');
      setAddDoc({ name: '', file: null });
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to add document.', { description: err.response?.data?.message || err.message });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-foreground">Add Permit Documents from County</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Manage and upload county permit documents.</p>
        </div>
        {showNewForm && (
          <Button type="button" variant="outline" size="sm" onClick={addRow} disabled={isSubmitting} className="gap-1.5 shrink-0">
            <Plus className="w-4 h-4" />
            Add Document
          </Button>
        )}
      </div>

      {!permitDetails?.base_fees_paid ? (
        <div className="flex items-start gap-3 border border-amber-200 bg-amber-50 text-amber-800 rounded-lg p-4 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          Base fees are pending. Please ask the user to pay the base fees to proceed.
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading documents…
        </div>
      ) : showNewForm ? (
        <>
          <div className="grid grid-cols-1 gap-3">
            {rows.map((row, idx) => (
              <CardShell
                key={row.id}
                index={idx + 1}
                onDelete={() => removeRow(row.id)}
                deleteDisabled={rows.length === 1 || isSubmitting}
              >
                <Input
                  placeholder="Document name"
                  value={row.name}
                  onChange={e => updateName(row.id, e.target.value)}
                  className="h-8 text-sm flex-1 min-w-0"
                  disabled={isSubmitting}
                />
                <input
                  ref={el => (fileRefs.current[row.id] = el)}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={e => handleFileChange(row.id, e)}
                />
                {row.file ? (
                  <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 px-2.5 py-1.5 rounded-md text-xs flex-1 min-w-0">
                    <FileText className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate flex-1">{row.file.name}</span>
                    <button onClick={() => attachFile(row.id, null)} disabled={isSubmitting} className="shrink-0 text-blue-400 hover:text-blue-700 transition">✕</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRefs.current[row.id]?.click()}
                    disabled={isSubmitting}
                    className="flex items-center justify-center gap-1.5 border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 px-2.5 py-1.5 rounded-md text-xs transition shrink-0"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Choose file
                  </button>
                )}
              </CardShell>
            ))}
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSubmit} disabled={isSubmitting} className="gap-2 min-w-[140px]">
              {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" />Uploading…</> : 'Upload & Submit'}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3">
            {countyPermit?.documents?.map((doc, idx) => (
              <CardShell
                key={doc._id}
                index={idx + 1}
                onDelete={() => setDeleteTarget({ docId: doc._id, docName: doc.name })}
                deleteDisabled={countyPermit.documents.length === 1}
              >
                <div className="h-8 flex-1 min-w-0 border border-border rounded-md px-3 flex items-center bg-muted/30 text-sm text-foreground select-none">
                  {doc.name}
                </div>
                <button
                  type="button"
                  onClick={() => viewDocument(doc.document)}
                  className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 px-2.5 py-1.5 rounded-md text-xs transition shrink-0"
                >
                  <FileText className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate max-w-[130px]">{shortFileName(doc.document)}</span>
                  <ExternalLink className="w-3 h-3 shrink-0" />
                </button>
              </CardShell>
            ))}
          </div>

          <div className="rounded-xl border border-dashed border-border bg-muted/20 flex flex-col overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Add New Document</span>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 px-4 py-3">
              <Input
                placeholder="Document name"
                value={addDoc.name}
                onChange={e => setAddDoc(prev => ({ ...prev, name: e.target.value }))}
                className="h-8 text-sm flex-1 min-w-0"
                disabled={isAdding}
              />
              <input
                ref={addFileRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.txt"
                onChange={e => {
                  const f = e.target.files[0];
                  if (f) setAddDoc(prev => ({ ...prev, file: f }));
                  e.target.value = '';
                }}
              />
              {addDoc.file ? (
                <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 px-2.5 py-1.5 rounded-md text-xs flex-1 min-w-0">
                  <FileText className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate flex-1">{addDoc.file.name}</span>
                  <button onClick={() => setAddDoc(prev => ({ ...prev, file: null }))} disabled={isAdding} className="shrink-0 text-blue-400 hover:text-blue-700 transition">✕</button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => addFileRef.current?.click()}
                  disabled={isAdding}
                  className="flex items-center justify-center gap-1.5 border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 px-2.5 py-1.5 rounded-md text-xs transition shrink-0"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Choose file
                </button>
              )}
              <Button size="sm" onClick={handleAddSingle} disabled={isAdding} className="gap-1.5 shrink-0 h-8">
                {isAdding ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Adding…</> : <><Plus className="w-3.5 h-3.5" />Add</>}
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Document</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to remove <strong>{deleteTarget?.docName}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isDeleting} className="gap-1.5">
              {isDeleting ? <><Loader2 className="w-4 h-4 animate-spin" />Removing…</> : 'Remove'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CountyPermitUpload;
